import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false); // Prevent multiple concurrent checks
  // sessionId: unique per browser-tab session; changes on explicit login so the
  // notifications modal knows when to re-appear.
  const [sessionId, setSessionId] = useState(
    () => sessionStorage.getItem('app_session_id') || null
  );

  // Verificar el estado de autenticación al cargar la aplicación
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    // Prevent multiple concurrent status checks
    if (isCheckingStatus) return;

    // Ensure this browser tab has a session ID (for the notifications modal).
    // If sessionStorage already has one we keep it (same tab reload → no re-trigger).
    if (!sessionStorage.getItem('app_session_id')) {
      const sid = `s_${Date.now()}`;
      sessionStorage.setItem('app_session_id', sid);
      setSessionId(sid);
    }
    
    try {
      setIsLoading(true);
      setIsCheckingStatus(true);
      
      // Verificar si hay datos de usuario en localStorage
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('csrfToken');
      
      if (storedUser && storedToken) {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setCsrfToken(storedToken);
        setIsAuthenticated(true);
        
        // Si tenemos datos válidos en localStorage, podemos evitar la verificación del servidor
        // a menos que sea necesaria (por ejemplo, después de un tiempo)
        const lastCheck = localStorage.getItem('lastAuthCheck');
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;
        
        if (lastCheck && (now - parseInt(lastCheck)) < fiveMinutes) {
          setIsLoading(false);
          setIsCheckingStatus(false);
          return;
        }
      }

      // Verificar con el servidor
      const response = await authAPI.checkStatus();
      localStorage.setItem('lastAuthCheck', Date.now().toString());
      
      if (response.data.authenticated) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        setCsrfToken(response.data.csrfToken);
        
        // Actualizar localStorage
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('csrfToken', response.data.csrfToken);
      } else {
        // Limpiar datos si no está autenticado
        logout();
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      logout();
    } finally {
      setIsLoading(false);
      setIsCheckingStatus(false);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      
      if (response.data.success) {
        const userData = response.data.user;
        const token = response.data.csrfToken;
        
        setUser(userData);
        setIsAuthenticated(true);
        setCsrfToken(token);
        
        // Guardar en localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('csrfToken', token);

        // Nueva sesión explícita: asignar un sessionId nuevo para que el modal
        // de notificaciones vuelva a mostrarse tras este login.
        const sid = `s_${Date.now()}`;
        sessionStorage.setItem('app_session_id', sid);
        sessionStorage.removeItem('notif_shown_ids'); // forzar re-aparición de todas las pendientes
        setSessionId(sid);
        
        return { success: true };
      } else {
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error de conexión';
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      // Intentar logout en el servidor si está autenticado
      if (isAuthenticated) {
        await authAPI.logout();
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Limpiar estado local
      setUser(null);
      setIsAuthenticated(false);
      setCsrfToken(null);
      
      // Limpiar localStorage y sessionStorage
      localStorage.removeItem('user');
      localStorage.removeItem('csrfToken');
      sessionStorage.removeItem('app_session_id');
      sessionStorage.removeItem('notif_shown_ids');
      setSessionId(null);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    csrfToken,
    sessionId,
    login,
    logout,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;